import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  PAYROLL_DEPARTMENTS,
  PAYROLL_DEPARTMENT_LABELS,
  PayrollDepartment,
  PayrollPosition,
  PayrollSettings,
  DEFAULT_PAYROLL_SETTINGS,
} from '../../types/finances';
import { positionMonthlyContribution } from '../../utils/financesForecast';
import { projectRaiseComp } from '../../utils/payrollCompSchedule';

interface PayrollColaModalProps {
  positions: PayrollPosition[];
  settings?: PayrollSettings;
  onClose: () => void;
  onApply: (args: {
    effectiveDate: Date;
    percent: number;
    label: string;
    departments: PayrollDepartment[] | 'all';
  }) => Promise<void>;
}

function toInputDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fromInputDate(s: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const PayrollColaModal: React.FC<PayrollColaModalProps> = ({
  positions,
  settings = DEFAULT_PAYROLL_SETTINGS,
  onClose,
  onApply,
}) => {
  const defaultEffective = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    d.setDate(1);
    return toInputDate(d);
  }, []);

  const [effectiveDate, setEffectiveDate] = useState(defaultEffective);
  const [percent, setPercent] = useState('3');
  const [label, setLabel] = useState(() => `COLA ${new Date().getFullYear() + 1}`);
  const [scopeAll, setScopeAll] = useState(true);
  const [departments, setDepartments] = useState<Set<PayrollDepartment>>(() => new Set(PAYROLL_DEPARTMENTS));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const numericPercent = useMemo(() => {
    const v = parseFloat(percent);
    return Number.isFinite(v) ? v : 0;
  }, [percent]);

  const targets = useMemo(() => {
    return positions.filter(p => {
      if (!p.isActive) return false;
      if (scopeAll) return true;
      return departments.has(p.department);
    });
  }, [positions, scopeAll, departments]);

  const preview = useMemo(() => {
    const effective = fromInputDate(effectiveDate);
    if (!effective || numericPercent <= 0 || targets.length === 0) {
      return { before: 0, after: 0, delta: 0 };
    }
    const month = new Date(effective.getFullYear(), effective.getMonth(), 1);
    const before = targets.reduce(
      (sum, p) => sum + positionMonthlyContribution(p, month, settings),
      0,
    );
    const after = targets.reduce((sum, p) => {
      const projected = projectRaiseComp(p, { kind: 'percent', amount: numericPercent });
      const next: PayrollPosition = { ...p, annualSalary: projected.annualSalary, hourlyRate: projected.hourlyRate };
      return sum + positionMonthlyContribution(next, month, settings);
    }, 0);
    return { before, after, delta: after - before };
  }, [effectiveDate, numericPercent, targets, settings]);

  const toggleDepartment = (dept: PayrollDepartment) => {
    setDepartments(prev => {
      const next = new Set(prev);
      if (next.has(dept)) next.delete(dept);
      else next.add(dept);
      return next;
    });
  };

  const handleApply = async () => {
    const effective = fromInputDate(effectiveDate);
    if (!effective) {
      setError('Effective date is required.');
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (effective < today) {
      setError('Effective date must be today or in the future.');
      return;
    }
    if (numericPercent <= 0) {
      setError('Enter a percent increase greater than zero.');
      return;
    }
    if (targets.length === 0) {
      setError('No active positions match the selected scope.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onApply({
        effectiveDate: effective,
        percent: numericPercent,
        label: label.trim() || `COLA ${effective.getFullYear()}`,
        departments: scopeAll ? 'all' : Array.from(departments),
      });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to schedule COLA');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fin-modal-overlay" onClick={onClose}>
      <div className="fin-modal" onClick={e => e.stopPropagation()} role="dialog" aria-labelledby="cola-modal-title">
        <div className="fin-modal-header">
          <h2 id="cola-modal-title">Schedule COLA</h2>
          <button type="button" className="fin-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="fin-modal-body">
          {error && <p className="fin-modal-error">{error}</p>}

          <div className="fin-modal-grid">
            <div className="fin-modal-field">
              <label className="fin-modal-label" htmlFor="cola-effective">Effective date</label>
              <input
                id="cola-effective"
                className="fin-modal-input"
                type="date"
                value={effectiveDate}
                onChange={e => setEffectiveDate(e.target.value)}
              />
            </div>
            <div className="fin-modal-field">
              <label className="fin-modal-label" htmlFor="cola-percent">Percent increase</label>
              <input
                id="cola-percent"
                className="fin-modal-input"
                type="number"
                min="0"
                step="0.1"
                value={percent}
                onChange={e => setPercent(e.target.value)}
              />
            </div>
            <div className="fin-modal-field full">
              <label className="fin-modal-label" htmlFor="cola-label">Label (optional)</label>
              <input
                id="cola-label"
                className="fin-modal-input"
                value={label}
                onChange={e => setLabel(e.target.value)}
                placeholder="COLA 2027"
              />
            </div>
          </div>

          <fieldset className="fin-modal-fieldset">
            <legend>Apply to</legend>
            <label className="fin-modal-toggle">
              <input type="radio" checked={scopeAll} onChange={() => setScopeAll(true)} />
              <span>All active positions ({positions.filter(p => p.isActive).length})</span>
            </label>
            <label className="fin-modal-toggle">
              <input type="radio" checked={!scopeAll} onChange={() => setScopeAll(false)} />
              <span>Selected departments</span>
            </label>
            {!scopeAll && (
              <div className="fin-cola-dept-grid">
                {PAYROLL_DEPARTMENTS.map(dept => (
                  <label key={dept} className="fin-modal-toggle">
                    <input
                      type="checkbox"
                      checked={departments.has(dept)}
                      onChange={() => toggleDepartment(dept)}
                    />
                    <span>{PAYROLL_DEPARTMENT_LABELS[dept]}</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          <div className="fin-cola-preview">
            <div className="fin-cola-preview-title">Preview at effective month</div>
            <p>
              <strong>{targets.length}</strong> position{targets.length === 1 ? '' : 's'} · loaded payroll{' '}
              <strong>{formatCurrency(preview.before)}</strong>/mo → <strong>{formatCurrency(preview.after)}</strong>/mo
              {preview.delta > 0 && (
                <span className="fin-cola-preview-delta"> (+{formatCurrency(preview.delta)}/mo)</span>
              )}
            </p>
          </div>
        </div>

        <div className="fin-modal-footer">
          <div className="fin-modal-footer-right">
            <button type="button" className="fin-secondary-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="fin-primary-btn" onClick={handleApply} disabled={saving}>
              {saving ? 'Scheduling…' : `Schedule COLA for ${targets.length} positions`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollColaModal;
